/**
 * MessaggiPanel — chat interna integrata nel Centro Comunicazioni
 * Il Diario di Bordo - Frontend
 *
 * Estratto da pages/MessagesPage.jsx. Riceve threadId e onSelectThread
 * come props (l'hub controlla l'URL e la navigation tra tab/thread).
 *
 * Split view desktop: lista thread + thread aperto con composer.
 * Stack mobile: lista a tutto schermo → click thread = conversazione
 * full-screen con back button (delega a onSelectThread(null)).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { MessageSquare, Send, Smile, ChevronLeft, AtSign, UserPlus, Users, X, Search, MoreVertical, Pencil, Trash2, Archive, Check, CheckCheck } from 'lucide-react'
import { chatApi, usersApi } from '../../services/api'
import { useMessageThread } from '../../hooks/useMessageThread'
import { useAuth } from '../../contexts/AuthContext'

const EMOJIS = ['👍', '❤️', '✅', '😂', '😮', '😢', '🙏', '🔥']

// ──────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────
const fullName = (u) => {
  if (!u) return ''
  const name = `${u.nome || ''} ${u.cognome || ''}`.trim()
  return name || 'Utente'
}

const initials = (u) => {
  if (!u) return '?'
  return `${u.nome?.[0] || ''}${u.cognome?.[0] || ''}`.toUpperCase() || '?'
}

// Normalizza qualsiasi reference utente in stringa hex pura.
// Supporta:
//  - string ObjectId puro
//  - oggetto user da /auth/me con campo `id` (alias mongoose virtual usato dal backend)
//  - oggetto user populato con campo `_id`
//  - EJSON serialization {$oid}
//  - ObjectId BSON nativo (fallback toString)
const normalizeUserId = (raw) => {
  if (!raw) return ''
  if (typeof raw === 'string') return raw
  if (raw.$oid) return String(raw.$oid)
  // IMPORTANTE: il backend espone l'utente come { id: ..., nome, ... } in
  // /auth/me e /auth/login (alias di _id). Quindi controlliamo `id` prima
  // di `_id` per non perdere quel caso.
  if (raw.id) return normalizeUserId(raw.id)
  if (raw._id) return normalizeUserId(raw._id)
  if (typeof raw.toString === 'function') {
    const s = raw.toString()
    if (s && s !== '[object Object]') return s
  }
  return ''
}

const getOtherParticipant = (thread, currentUserId) => {
  if (!thread || thread.type !== 'direct') return null
  const myId = String(currentUserId || '')
  // Safety: se non sappiamo chi siamo, non possiamo identificare "l'altro"
  // (evitiamo di scegliere il primo partecipante che spesso è proprio l'utente
  // loggato e produce l'header sbagliato "stai chattando con te stesso").
  if (!myId) return null
  const other = (thread.participants || []).find((p) => {
    const pid = normalizeUserId(p.userId)
    return pid && pid !== myId
  })
  return other?.userId || null
}

const getThreadDisplayName = (thread, currentUserId) => {
  if (!thread) return 'Conversazione'
  if (thread.type === 'direct') {
    const peer = getOtherParticipant(thread, currentUserId)
    return peer ? fullName(peer) : 'Conversazione diretta'
  }
  return thread.name || 'Gruppo'
}

// Estrae l'ID del sender da un message, controllando in ordine:
// m.sender._id (populated nuovo schema) → m.senderId (string o populated)
const senderIdOf = (m) => {
  if (!m) return ''
  if (m.sender) {
    const fromSender = normalizeUserId(m.sender)
    if (fromSender) return fromSender
  }
  return normalizeUserId(m.senderId)
}

// Estrae info sender per il nome dentro la bolla (m.sender preferito,
// fallback su senderId populated se non normalizzato)
const senderInfoOf = (m) => {
  if (!m) return null
  if (m.sender && (m.sender.nome || m.sender.cognome)) return m.sender
  if (m.senderId && typeof m.senderId === 'object' && (m.senderId.nome || m.senderId.cognome)) {
    return m.senderId
  }
  return null
}

export default function MessaggiPanel({ threadId = null, onSelectThread }) {
  const { user } = useAuth()
  const myUserId = normalizeUserId(user)
  // Helper interno: tutti i posti che prima facevano navigate('/messaggi/...')
  // ora delegano all'hub via onSelectThread(idOrNull).
  const goToThread = (id) => {
    if (typeof onSelectThread === 'function') onSelectThread(id)
  }
  const [threads, setThreads] = useState([])
  const [loadingThreads, setLoadingThreads] = useState(true)
  const [showNewChat, setShowNewChat] = useState(false)

  const loadThreads = useCallback(async () => {
    setLoadingThreads(true)
    try {
      const { data } = await chatApi.listThreads()
      setThreads(data?.data || [])
    } finally {
      setLoadingThreads(false)
    }
  }, [])

  useEffect(() => { loadThreads() }, [loadThreads])

  const handleStartChat = async (userId) => {
    try {
      const { data } = await chatApi.createDirect(userId)
      const newThread = data?.data
      setShowNewChat(false)
      await loadThreads()
      if (newThread?._id) goToThread(newThread._id)
    } catch (err) {
      console.error('[MessaggiPanel] createDirect error:', err)
      alert('Impossibile iniziare la conversazione: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleCreateGroup = async ({ name, participantIds }) => {
    try {
      const { data } = await chatApi.createGroup({ name, participantIds })
      const newThread = data?.data
      setShowNewChat(false)
      await loadThreads()
      if (newThread?._id) goToThread(newThread._id)
    } catch (err) {
      console.error('[MessaggiPanel] createGroup error:', err)
      alert('Impossibile creare il gruppo: ' + (err.response?.data?.error || err.message))
    }
  }

  const handleArchive = async (id) => {
    if (!window.confirm('Archiviare questa conversazione? Sparirà dalla lista.')) return
    try {
      await chatApi.archiveThread(id)
      await loadThreads()
      if (threadId === id) goToThread(null)
    } catch (err) {
      alert('Impossibile archiviare: ' + (err.response?.data?.error || err.message))
    }
  }

  const activeThread = threads.find((t) => t._id === threadId)

  return (
    // h-full: il container parent (hub) gestisce l'altezza disponibile
    // (resto schermo dopo header + tab bar). Il composer resta sempre
    // visibile sopra la tastiera virtuale perché il parent usa 100dvh.
    <div className="flex bg-white dark:bg-gray-900 h-full">
      {/* Sidebar lista thread (nasconde su mobile quando thread attivo) */}
      <aside className={`${threadId ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 md:border-r border-gray-200 dark:border-gray-700`}>
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Conversazioni</p>
          <button
            onClick={() => setShowNewChat(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            title="Inizia una nuova conversazione"
          >
            <UserPlus className="h-4 w-4" />
            <span className="hidden sm:inline">Nuova</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingThreads && <p className="p-4 text-sm text-gray-500">Caricamento...</p>}
          {!loadingThreads && threads.length === 0 && (
            <p className="p-6 text-center text-sm text-gray-500">
              Nessuna conversazione. Inizia da un profilo utente.
            </p>
          )}
          <ul>
            {threads.map((t) => {
              const displayName = getThreadDisplayName(t, myUserId)
              const peer = getOtherParticipant(t, myUserId)
              const isGroup = t.type !== 'direct'
              return (
                <li key={t._id} className="group/thread relative">
                  <button
                    onClick={() => goToThread(t._id)}
                    className={`w-full text-left p-3 pr-10 hover:bg-gray-50 dark:hover:bg-gray-800 border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                      t._id === threadId ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 text-sm font-medium flex-shrink-0">
                        {isGroup ? <Users className="h-5 w-5" /> : (peer ? initials(peer) : '?')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {displayName}
                        </p>
                        {t.lastMessagePreview && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {t.lastMessagePreview}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                  {/* Bottone archivia visibile in hover */}
                  <button
                    onClick={() => handleArchive(t._id)}
                    className="absolute top-2 right-2 p-1.5 rounded opacity-0 group-hover/thread:opacity-100 hover:bg-gray-200 dark:hover:bg-gray-700 transition-opacity"
                    title="Archivia conversazione"
                    aria-label="Archivia"
                  >
                    <Archive className="h-4 w-4 text-gray-500" />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </aside>

      {/* Area conversazione */}
      <main className={`${threadId ? 'flex' : 'hidden md:flex'} flex-col flex-1`}>
        {!threadId ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p>Seleziona una conversazione</p>
              <button
                onClick={() => setShowNewChat(true)}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <UserPlus className="h-4 w-4" />
                Inizia una conversazione
              </button>
            </div>
          </div>
        ) : (
          <ThreadView
            key={threadId}
            thread={activeThread}
            threadId={threadId}
            onBack={() => goToThread(null)}
            onArchive={() => handleArchive(threadId)}
          />
        )}
      </main>

      {showNewChat && (
        <NewChatModal
          onClose={() => setShowNewChat(false)}
          onSelectDirect={handleStartChat}
          onCreateGroup={handleCreateGroup}
        />
      )}
    </div>
  )
}

function ThreadView({ thread, threadId, onBack, onArchive }) {
  const { user } = useAuth()
  const { messages, loading, sendMessage, editMessage, deleteMessage, markRead, addReaction, removeReaction } = useMessageThread(threadId)
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [showEmoji, setShowEmoji] = useState(null)
  const [openMenu, setOpenMenu] = useState(null)        // id messaggio col menu ⋮ aperto
  const [editingId, setEditingId] = useState(null)
  const [editBody, setEditBody] = useState('')
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false)
  // Selezione multipla per batch delete (solo messaggi propri)
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [batchDeleting, setBatchDeleting] = useState(false)
  const scrollRef = useRef(null)
  // Stato dello scroll: l'utente è "in fondo"? (true all'apertura).
  // Aggiornato in onScroll: < 100px dal bottom = in fondo.
  const isAtBottomRef = useRef(true)
  // ID dell'ultimo messaggio già processato dall'effect di auto-scroll.
  // Serve per distinguere "nuovo messaggio in coda" da "stato aggiornato su
  // un messaggio esistente" (delivery/read/reaction/edit/delete).
  const lastSeenMessageIdRef = useRef(null)
  // ID dell'ultimo messaggio già notificato al backend come "letto". Evita
  // di richiamare PUT /threads/:id/read in loop quando arrivano eventi
  // socket (chat:thread-read, delivery-update, reaction, edit) che
  // cambiano l'oggetto messages ma non aggiungono nulla in coda.
  const lastMarkedReadIdRef = useRef(null)

  // Esci dalla selection mode e resetta lo stato scroll/read quando cambi thread
  useEffect(() => {
    setSelectionMode(false)
    setSelectedIds(new Set())
    isAtBottomRef.current = true
    lastSeenMessageIdRef.current = null
    lastMarkedReadIdRef.current = null
  }, [threadId])

  // Normalizza l'ID utente loggato una volta: tutti i confronti devono
  // usare lo stesso formato canonico (stringa hex pura).
  const myUserId = normalizeUserId(user)
  const isGroup = thread?.type !== 'direct'
  const displayName = getThreadDisplayName(thread, myUserId)
  const peer = getOtherParticipant(thread, myUserId)


  // chiudi menu cliccando fuori
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('[data-menu-trigger]') && !e.target.closest('[data-menu-content]')) {
        setOpenMenu(null)
        setHeaderMenuOpen(false)
      }
    }
    if (openMenu || headerMenuOpen) {
      document.addEventListener('mousedown', handler)
      return () => document.removeEventListener('mousedown', handler)
    }
  }, [openMenu, headerMenuOpen])

  // Auto-scroll intelligente: scrolla in fondo solo quando ha senso, così
  // l'utente può scrollare in alto a leggere i messaggi vecchi senza che
  // un nuovo messaggio (o un update di reazione/delivery) lo riporti giù.
  useEffect(() => {
    if (!scrollRef.current || messages.length === 0) return
    const lastMsg = messages[messages.length - 1]
    const newLastId = lastMsg._id
    const isFirstLoad = lastSeenMessageIdRef.current === null
    const hasNewLastMessage = !isFirstLoad && lastSeenMessageIdRef.current !== newLastId
    const lastIsMine = senderIdOf(lastMsg) === myUserId
    lastSeenMessageIdRef.current = newLastId

    // Scrolla in fondo se: primo render del thread, oppure è arrivato un
    // messaggio nuovo in coda E (l'utente era già in fondo OR l'ha inviato lui).
    // Update di delivery/read/reaction/edit/delete NON cambiano newLastId,
    // quindi hasNewLastMessage=false → niente scroll forzato.
    if (isFirstLoad || (hasNewLastMessage && (isAtBottomRef.current || lastIsMine))) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight
        }
      })
    }

    // Notifica al backend solo se l'ID dell'ultimo messaggio è effettivamente
    // cambiato. Senza questo dedupe, ogni evento socket (chat:thread-read,
    // delivery-update, reaction) ricrea l'array messages → l'effect chiamava
    // markRead con lo stesso ID → backend emetteva chat:thread-read agli altri
    // → loro entravano nello stesso loop = traffico esponenziale.
    if (lastMarkedReadIdRef.current !== newLastId) {
      lastMarkedReadIdRef.current = newLastId
      markRead(newLastId)
    }
  }, [messages, markRead, myUserId])

  // Traccia se l'utente è "in fondo" alla scroll area (threshold 100px) per
  // decidere se l'auto-scroll del prossimo messaggio deve fare snap o no.
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 100
  }, [])

  const handleSend = async () => {
    const text = body.trim()
    if (!text || sending) return
    setSending(true)
    setSendError(null)
    try {
      await sendMessage(text)
      setBody('')
    } catch (err) {
      const apiError = err.response?.data?.error || err.message
      setSendError(apiError || 'Errore invio messaggio')
      // mantiene il testo nel composer così l'utente può ritentare
    } finally {
      setSending(false)
    }
  }

  const handleToggleReaction = async (m, emoji) => {
    setShowEmoji(null)
    const myReaction = (m.reactions || []).find(
      (r) => r.emoji === emoji && r.userIds?.some((u) => normalizeUserId(u) === myUserId)
    )
    try {
      if (myReaction) {
        await removeReaction(m._id, emoji)
      } else {
        await addReaction(m._id, emoji)
      }
    } catch (err) {
      console.error('[chat] reaction error:', err)
    }
  }

  const handleDelete = async (id) => {
    setOpenMenu(null)
    if (!window.confirm('Eliminare il messaggio?')) return
    try {
      await deleteMessage(id)
    } catch (err) {
      alert('Impossibile eliminare: ' + (err.response?.data?.error || err.message))
    }
  }

  // ── Selezione multipla ─────────────────────────────────────
  const enterSelection = (id) => {
    setOpenMenu(null)
    setSelectionMode(true)
    setSelectedIds(new Set([id]))
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      // Esce automaticamente dalla selection mode se non c'è nulla selezionato
      if (next.size === 0) setSelectionMode(false)
      return next
    })
  }

  const cancelSelection = () => {
    setSelectionMode(false)
    setSelectedIds(new Set())
  }

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0 || batchDeleting) return
    const count = selectedIds.size
    if (!window.confirm(`Eliminare ${count} messaggi selezionat${count === 1 ? 'o' : 'i'}?`)) return
    setBatchDeleting(true)
    const failed = []
    for (const id of selectedIds) {
      try {
        await deleteMessage(id)
      } catch (err) {
        failed.push(id)
      }
    }
    setBatchDeleting(false)
    if (failed.length > 0) {
      alert(`${count - failed.length} eliminati, ${failed.length} falliti (forse non sono tuoi messaggi).`)
    }
    cancelSelection()
  }

  const startEdit = (m) => {
    setOpenMenu(null)
    setEditingId(m._id)
    setEditBody(m.body || '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditBody('')
  }

  const confirmEdit = async () => {
    const text = editBody.trim()
    if (!text || !editingId) return
    try {
      await editMessage(editingId, text)
      cancelEdit()
    } catch (err) {
      alert('Impossibile modificare: ' + (err.response?.data?.error || err.message))
    }
  }

  // Calcola lo stato di editabilità: { canEdit, reason }
  // canEdit=true → modifica permessa; false → motivo nella reason (per tooltip).
  // Ritorna null se il messaggio non è del corrente utente (in tal caso il
  // bottone "Modifica" non deve apparire affatto).
  const getEditStatus = (m) => {
    if (!m || m.deleted) return null
    if (senderIdOf(m) !== myUserId) return null
    if (!m.editableUntil || new Date(m.editableUntil) <= new Date()) {
      return { canEdit: false, reason: 'Tempo scaduto (solo entro 15 minuti dall\'invio)' }
    }
    if (m.readByOthers) {
      return { canEdit: false, reason: 'Già letto: non più modificabile' }
    }
    return { canEdit: true, reason: '' }
  }

  return (
    <>
      {selectionMode ? (
        <header className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 bg-primary-50 dark:bg-primary-900/20">
          <button onClick={cancelSelection} className="p-1 rounded hover:bg-primary-100 dark:hover:bg-primary-900/40" aria-label="Annulla selezione">
            <X className="h-5 w-5 text-primary-700 dark:text-primary-300" />
          </button>
          <h2 className="flex-1 font-semibold text-primary-700 dark:text-primary-300">
            {selectedIds.size} selezionat{selectedIds.size === 1 ? 'o' : 'i'}
          </h2>
          <button
            onClick={handleBatchDelete}
            disabled={selectedIds.size === 0 || batchDeleting}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Elimina selezionati"
          >
            <Trash2 className="h-4 w-4" />
            <span className="hidden sm:inline">{batchDeleting ? 'Elimino...' : 'Elimina'}</span>
          </button>
        </header>
      ) : (
        <header className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <button onClick={onBack} className="md:hidden p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 text-sm font-medium flex-shrink-0">
            {isGroup ? <Users className="h-4 w-4" /> : (peer ? initials(peer) : '?')}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 dark:text-white truncate">
              {displayName}
            </h2>
            {isGroup && Array.isArray(thread?.participants) && (
              <p className="text-xs text-gray-500 truncate">
                {thread.participants.length} partecipanti
              </p>
            )}
          </div>
          <div className="relative">
            <button
              data-menu-trigger
              onClick={() => setHeaderMenuOpen((o) => !o)}
              className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Opzioni conversazione"
            >
              <MoreVertical className="h-5 w-5 text-gray-500" />
            </button>
            {headerMenuOpen && (
              <div
                data-menu-content
                className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-30"
              >
                <button
                  onClick={() => { setHeaderMenuOpen(false); onArchive?.() }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  <Archive className="h-4 w-4" />
                  Archivia conversazione
                </button>
              </div>
            )}
          </div>
        </header>
      )}

      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 dark:bg-gray-900/50">
        {loading && messages.length === 0 && (
          <p className="text-center text-sm text-gray-500">Caricamento...</p>
        )}
        {messages.map((m) => {
          const isMine = senderIdOf(m) === myUserId
          const senderInfo = senderInfoOf(m)
          const senderLabel = isMine ? 'Tu' : (senderInfo ? fullName(senderInfo) : 'Utente')
          const isEditing = editingId === m._id
          const timeStr = new Date(m.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
          const hasReactions = Array.isArray(m.reactions) && m.reactions.length > 0
          const isSelected = selectedIds.has(m._id)
          // In selection mode: solo i propri messaggi sono interagibili
          // (eliminabili). Per gli altri mostro la bolla disabilitata.
          const isSelectable = selectionMode && isMine

          // Stili condivisi per testi secondari dentro la bolla (orario, nome,
          // chip reazioni) — devono restare leggibili sia su sfondo blu (mine)
          // che bianco (others).
          const subTextCls = isMine ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'

          return (
            <div
              key={m._id}
              className={`flex items-center gap-2 ${isMine ? 'justify-end' : 'justify-start'} group ${
                selectionMode && !isMine ? 'opacity-50' : ''
              } ${isSelectable ? 'cursor-pointer' : ''}`}
              onClick={isSelectable ? () => toggleSelect(m._id) : undefined}
            >
              {selectionMode && isMine && (
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center order-2 ml-2 ${
                    isSelected
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                  }`}
                  aria-hidden="true"
                >
                  {isSelected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                </span>
              )}
              <div className={`max-w-[75%] ${isSelected ? 'ring-2 ring-primary-500 rounded-2xl' : ''}`}>
                {isEditing ? (
                  <div className="px-3 py-2 rounded-2xl bg-white dark:bg-gray-800 border border-primary-300 dark:border-primary-700">
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={2}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          confirmEdit()
                        } else if (e.key === 'Escape') {
                          cancelEdit()
                        }
                      }}
                      className="w-full text-sm resize-none bg-transparent focus:outline-none dark:text-white"
                    />
                    <div className="flex justify-end gap-2 mt-1 text-xs">
                      <button onClick={cancelEdit} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                        Annulla
                      </button>
                      <button onClick={confirmEdit} className="text-primary-600 hover:text-primary-700 font-medium">
                        Salva
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`px-3 py-2 rounded-2xl ${
                      isMine
                        ? 'bg-primary-600 text-white rounded-br-md'
                        : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-md'
                    } ${m.deleted ? 'italic opacity-60' : ''}`}
                  >
                    {/* Riga 1: nome (Tu o senderLabel) + orario + spunte + reazioni + azioni */}
                    <div className="flex items-center gap-1.5 flex-wrap text-xs">
                      <span className={`font-semibold ${isMine ? 'text-white' : 'text-gray-800 dark:text-gray-200'}`}>
                        {senderLabel}
                      </span>
                      <span className={subTextCls}>·</span>
                      <span className={subTextCls}>{timeStr}</span>
                      {/* Spunte read receipts (solo sui miei messaggi, non eliminati) */}
                      {isMine && !m.deleted && (
                        <ReadReceipt
                          delivered={m.deliveredToOthers}
                          read={m.readByOthers}
                          className={subTextCls}
                        />
                      )}
                      {m.edited && (
                        <span className={`${subTextCls} italic`}>(modificato)</span>
                      )}

                      {/* Chip reazioni inline */}
                      {hasReactions && !m.deleted && !selectionMode && m.reactions.map((r) => {
                        const mineReaction = r.userIds?.some((u) => normalizeUserId(u) === myUserId)
                        return (
                          <button
                            key={r.emoji}
                            onClick={(e) => { e.stopPropagation(); handleToggleReaction(m, r.emoji) }}
                            className={`text-[11px] leading-none px-1.5 py-0.5 rounded-full border ${
                              isMine
                                ? mineReaction
                                  ? 'bg-white/30 border-white/50 text-white'
                                  : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
                                : mineReaction
                                  ? 'bg-primary-100 border-primary-300 dark:bg-primary-900/40 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                            }`}
                          >
                            {r.emoji} {r.count}
                          </button>
                        )
                      })}

                      {/* Azioni (Smile + menu ⋮) push a destra, visibili in hover.
                          Nascoste durante selection mode (il click è dedicato al toggle). */}
                      {!m.deleted && !selectionMode && (
                        <div className={`ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${subTextCls}`}>
                          <button
                            onClick={() => setShowEmoji(showEmoji === m._id ? null : m._id)}
                            className={isMine ? 'hover:text-white' : 'hover:text-gray-700 dark:hover:text-gray-300'}
                            aria-label="Reagisci"
                          >
                            <Smile className="h-3.5 w-3.5" />
                          </button>
                          {isMine && (
                            <div className="relative">
                              <button
                                data-menu-trigger
                                onClick={() => setOpenMenu(openMenu === m._id ? null : m._id)}
                                className="hover:text-white"
                                aria-label="Azioni messaggio"
                              >
                                <MoreVertical className="h-3.5 w-3.5" />
                              </button>
                              {openMenu === m._id && (() => {
                                const editStatus = getEditStatus(m)
                                return (
                                  <div
                                    data-menu-content
                                    className="absolute right-0 bottom-full mb-1 w-52 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-30 overflow-hidden"
                                  >
                                    {editStatus && (
                                      <button
                                        onClick={editStatus.canEdit ? () => startEdit(m) : undefined}
                                        disabled={!editStatus.canEdit}
                                        title={editStatus.canEdit ? '' : editStatus.reason}
                                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                                          editStatus.canEdit
                                            ? 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 cursor-pointer'
                                            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                        }`}
                                      >
                                        <Pencil className="h-3.5 w-3.5 flex-shrink-0" />
                                        <div className="flex flex-col items-start min-w-0">
                                          <span>Modifica</span>
                                          {!editStatus.canEdit && (
                                            <span className="text-[10px] leading-tight opacity-80 truncate w-full">
                                              {editStatus.reason}
                                            </span>
                                          )}
                                        </div>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDelete(m._id)}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                      Elimina
                                    </button>
                                    <button
                                      onClick={() => enterSelection(m._id)}
                                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border-t border-gray-100 dark:border-gray-700"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                      Seleziona
                                    </button>
                                  </div>
                                )
                              })()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Riga 2: corpo del messaggio */}
                    <p className="text-sm whitespace-pre-wrap break-words mt-1">
                      {m.deleted ? '[messaggio eliminato]' : m.body}
                    </p>
                  </div>
                )}

                {/* Emoji picker (popup sotto la bolla quando si clicca Smile) */}
                {showEmoji === m._id && (
                  <div className={`flex gap-1 mt-1 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full shadow ${isMine ? 'justify-end' : ''}`}>
                    {EMOJIS.map((e) => (
                      <button
                        key={e}
                        onClick={() => handleToggleReaction(m, e)}
                        className="hover:scale-125 transition-transform"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <footer className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {sendError && (
          <div className="mb-2 px-3 py-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded flex items-center justify-between">
            <span>{sendError}</span>
            <button onClick={() => setSendError(null)} className="ml-2 text-red-700 hover:text-red-900" aria-label="Chiudi">×</button>
          </div>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="Scrivi un messaggio..."
            rows={1}
            className="flex-1 px-3 py-2 border rounded-lg resize-none dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-primary-500 max-h-32"
          />
          <button
            onClick={handleSend}
            disabled={!body.trim() || sending}
            className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Invia"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </footer>
    </>
  )
}

// ──────────────────────────────────────────────────────────────
// READ RECEIPT — spunte WhatsApp-style sui propri messaggi
// ✓     = inviato (server-side OK)
// ✓✓    = consegnato (almeno un destinatario ha confermato delivery via WS)
// ✓✓ blu = letto (almeno un destinatario ha aperto la chat dopo l'invio)
// ──────────────────────────────────────────────────────────────
function ReadReceipt({ delivered, read, className = '' }) {
  if (read) {
    // 2 spunte blu (override colore parent)
    return (
      <span title="Letto" className="inline-flex items-center text-sky-300">
        <CheckCheck className="h-3.5 w-3.5" />
      </span>
    )
  }
  if (delivered) {
    // 2 spunte grigie
    return (
      <span title="Consegnato" className={`inline-flex items-center ${className}`}>
        <CheckCheck className="h-3.5 w-3.5" />
      </span>
    )
  }
  // 1 spunta grigia (inviato al server)
  return (
    <span title="Inviato" className={`inline-flex items-center ${className}`}>
      <Check className="h-3.5 w-3.5" />
    </span>
  )
}

// ──────────────────────────────────────────────────────────────
// NEW CHAT MODAL — 2 tab: Singola (1:1) | Gruppo (channel multi-utente)
// ──────────────────────────────────────────────────────────────
function NewChatModal({ onClose, onSelectDirect, onCreateGroup }) {
  const { user: currentUser } = useAuth()
  const myUserId = normalizeUserId(currentUser)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [mode, setMode] = useState('direct')  // 'direct' | 'group'
  const [selectedIds, setSelectedIds] = useState([])
  const [groupName, setGroupName] = useState('')
  const [creatingGroup, setCreatingGroup] = useState(false)

  useEffect(() => {
    setLoading(true)
    usersApi.list({ attivo: 'true' })
      .then(({ data }) => {
        const list = data?.data?.users || data?.data || []
        const filtered = list.filter((u) => normalizeUserId(u) !== myUserId)
        setUsers(filtered)
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Impossibile caricare la lista utenti')
      })
      .finally(() => setLoading(false))
  }, [myUserId])

  const filteredUsers = users.filter((u) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      u.nome?.toLowerCase().includes(q) ||
      u.cognome?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    )
  })

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])
  }

  const roleLabel = (role) => {
    if (role === 'coordinatore') return 'Coordinatore'
    if (role === 'responsabile') return 'Responsabile'
    return 'Educatore'
  }

  const handleCreateGroup = async () => {
    if (creatingGroup) return
    if (!groupName.trim()) {
      alert('Inserisci un nome per il gruppo')
      return
    }
    if (selectedIds.length === 0) {
      alert('Seleziona almeno un partecipante')
      return
    }
    setCreatingGroup(true)
    try {
      await onCreateGroup({ name: groupName.trim(), participantIds: selectedIds })
    } finally {
      setCreatingGroup(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary-600" />
            Nuova conversazione
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs Singola / Gruppo */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => { setMode('direct'); setSelectedIds([]) }}
            className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              mode === 'direct'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Singola
          </button>
          <button
            onClick={() => setMode('group')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              mode === 'group'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Gruppo
          </button>
        </div>

        {mode === 'group' && (
          <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
            <input
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Nome del gruppo (es. Educatori turno mattina)"
              maxLength={100}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {selectedIds.length > 0 && (
              <p className="text-xs text-gray-500">
                {selectedIds.length} partecipante{selectedIds.length === 1 ? '' : 'i'} selezionat{selectedIds.length === 1 ? 'o' : 'i'}
              </p>
            )}
          </div>
        )}

        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome o email..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus={mode === 'direct'}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <p className="p-6 text-center text-sm text-gray-500">Caricamento utenti...</p>}
          {error && <p className="p-6 text-center text-sm text-red-600">{error}</p>}
          {!loading && !error && filteredUsers.length === 0 && (
            <p className="p-6 text-center text-sm text-gray-500">
              {search ? 'Nessun utente trovato' : 'Nessun utente disponibile'}
            </p>
          )}
          <ul>
            {filteredUsers.map((u) => {
              const isSelected = selectedIds.includes(u._id)
              const onClick = mode === 'direct'
                ? () => onSelectDirect(u._id)
                : () => toggleSelect(u._id)
              return (
                <li key={u._id}>
                  <button
                    onClick={onClick}
                    className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 border-gray-100 dark:border-gray-700 ${
                      mode === 'group' && isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {mode === 'group' && (
                        <span className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-primary-600 border-primary-600 text-white'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                        </span>
                      )}
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-700 dark:text-primary-300 text-sm font-medium flex-shrink-0">
                        {initials(u)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {fullName(u)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {roleLabel(u.role)} · {u.email}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        {mode === 'group' && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Annulla
            </button>
            <button
              onClick={handleCreateGroup}
              disabled={creatingGroup || !groupName.trim() || selectedIds.length === 0}
              className="px-4 py-2 text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creatingGroup ? 'Creazione...' : 'Crea gruppo'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
